import actionTypes from 'state/actionTypes'
import { setToken, clearToken, getToken } from 'core/helpers'
import jwt from 'jwt-decode'
import { IJwtToken, IUserState } from 'types'
import { AnyAction } from 'redux'
import { MultiFactorVerifiedDto, AuthenticationDto } from '@v1UsersApi'

const initialState: IUserState = {
    authenticated: false,
    userId: 0,
    roleId: 0,
    token: '',
    permissions: [],
    authErrors: [],
    //is multi-factor required on login?
    multiFactorLoginEnabled: false,
    //Has the user completed the mfa verification process yet for this session?
    multiFactorConfirmed: false,
    //multi-factor guid is returned after mfa verification has been completed in the API response (this is short term 2fa used for actions such as changing password or anything of high security)
    multiFactorGuid: '',
    //when does multi-factor guid expire (typically 10 minutes from the verification)
    multiFactorGuidExpireDate: new Date(),
    homePage: '',
}

function getTokenUser(token: string) {
    const tokenUser: IJwtToken = jwt(token)
    return {
        userId: parseInt(tokenUser.uid, 10),
        roleId: parseInt(tokenUser.roleid, 10),
        dealerId: tokenUser.did,
        dealerGroupId: tokenUser.dgid,
    }
}

function getPayload(token: string, payload: AuthenticationDto) {

    let homePage = '/'
    if (process.env.REACT_APP_ENABLE_OLD_SA_INTEGRATION === 'true') {
        homePage = payload.homePage
            ? payload.homePage.endsWith('aspx')
                ? `${process.env.REACT_APP_OLD_SYS_ADMIN_URL}/web/${payload.homePage}`
                : payload.homePage
            : `${process.env.REACT_APP_OLD_SYS_ADMIN_URL}/web/Home.aspx`
    }

    return {
        token: token,
        permissions: payload.permissions!,
        multiFactorConfirmed: payload.multiFactorConfirmed,
        multiFactorLoginEnabled: payload.multiFactorLoginEnabled,
        homePage: homePage,
    }
}

function getUserFromAuthentication(state: IUserState, action: AnyAction) {
    const payload: AuthenticationDto = action.payload
    const user: IUserState = {
        ...getTokenUser(payload.token!),
        ...getPayload(payload.token!, payload),
        authenticated: true,
        authErrors: [],
        multiFactorGuid: state.multiFactorGuid,
        multiFactorGuidExpireDate: state.multiFactorGuidExpireDate,
    }
    return user
}

function getUserFromVerify(state: IUserState, action: AnyAction) {
    const payload: AuthenticationDto = action.payload
    var token = getToken()
    const user: IUserState = {
        ...getTokenUser(token),
        ...getPayload(token, payload),
        authenticated: true,
        authErrors: [],
        multiFactorGuid: state.multiFactorGuid,
        multiFactorGuidExpireDate: state.multiFactorGuidExpireDate,
    }
    return user
}

export default function userReducer(state = initialState, action: AnyAction): IUserState {
    switch (action.type) {
        // login
        case actionTypes.LOGIN_START:
            clearToken()
            return { ...initialState }

        case actionTypes.LOGIN_SUCCESS:
            {
                const user = getUserFromAuthentication(state, action)
                setToken(action.payload.token)
                return { ...state, ...user }
            }

        case actionTypes.LOGIN_FAILED:
            clearToken()
            return { ...initialState, authErrors: action.payload.errors }

        // logout
        case actionTypes.LOGOUT_START:
            return { ...state }

        case actionTypes.LOGOUT_SUCCESS:
            clearToken()
            return { ...initialState }

        case actionTypes.LOGOUT_FAILED:
            return { ...state, authErrors: action.payload.errors }

        case actionTypes.EXPIRE_SESSION_SUCCESS:
            return { ...initialState, authErrors: action.payload.errors }

        case actionTypes.EXPIRE_SESSION_FAILED:
            return { ...initialState, authErrors: action.payload.errors }

        // verify token
        case actionTypes.VERIFY_TOKEN_START:
            return { ...state }

        case actionTypes.VERIFY_TOKEN_SUCCESS:
            {
                const user = getUserFromVerify(state, action)
                setToken(user.token)
                return { ...state, ...user }
            }

        case actionTypes.VERIFY_TOKEN_FAILED:
            return { ...initialState, authErrors: action.payload.errors }

        case actionTypes.USER_MULTI_FACTOR_VERIFIED:
            const dto: MultiFactorVerifiedDto = action.payload
            return {
                ...state,
                multiFactorConfirmed: dto.multiFactorConfirmed,
            }

        default:
            return state
    }
};
